/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { FileStoreMappingsServiceConfiguration } from './FileStoreMappings.service.config';
import {
  CreateFileStoreMappingResponse,
  GetFileStoreMappingListResponse,
  GetFileStoreMappingResponse,
} from './FileStoreMappings.service.defs';
import {
  CreateFileStoreMappingDto,
  DeleteFileStoreMappingDto,
  GetFileStoreMappingDto,
  GetFileStoreMappingListDto,
  UpdateFileStoreMappingDto,
} from './FileStoreMappings.service.dto';

export class FileStoreMappingsService extends BaseService {
  public constructor(configuration: FileStoreMappingsServiceConfiguration) {
    super(configuration);
  }

  protected serviceHeaders = (): Headers => {
    const baseHeaders = new Headers({ 'Content-Type': 'application/json' });
    return baseHeaders;
  };

  getBaseServiceUrl(): string | null {
    const baseServiceUrl = this.getServiceUrlByTenant(`file-storage-management`);

    if (!baseServiceUrl) {
      return null;
    }

    return `${baseServiceUrl}/mappings`;
  }

  createMapping(dto: CreateFileStoreMappingDto): Observable<CreateFileStoreMappingResponse | null> {
    const requestUrl = this.getBaseServiceUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const request = this.httpService
      .post<CreateFileStoreMappingDto, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: dto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<CreateFileStoreMappingResponse> => {
          return this._handleResponseError<CreateFileStoreMappingResponse>(value);
        }),
      );
    return request;
  }

  getMappingList(dto: GetFileStoreMappingListDto): Observable<GetFileStoreMappingListResponse | null> {
    const { filterAddress, filterSource, pageLimit } = dto;

    const queryParams: Array<string> = [];

    if (filterAddress) {
      queryParams.push(`${encodeURIComponent('filter[address][beginsWith]')}=${filterAddress}`);
    }

    if (filterSource) {
      queryParams.push(`${encodeURIComponent('filter[source][equals]')}=${filterSource}`);
    }

    if (pageLimit) {
      queryParams.push(`${encodeURIComponent('limit')}=${pageLimit}`);
    }

    const serviceUrl = this.getBaseServiceUrl();

    if (!serviceUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, serviceUrl);
    const request = this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<GetFileStoreMappingListResponse> => {
          return this._handleResponseError<GetFileStoreMappingListResponse>(value);
        }),
      );
    return request;
  }

  getMapping(dto: GetFileStoreMappingDto): Observable<GetFileStoreMappingResponse | null> {
    const { id } = dto;
    const baseURL = this.getBaseServiceUrl();

    if (!baseURL) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }
    const requestUrl = `${baseURL}/${id}`;
    const request = this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<GetFileStoreMappingResponse> => {
          return this._handleResponseError<GetFileStoreMappingResponse>(value);
        }),
      );
    return request;
  }

  updateMapping(dto: UpdateFileStoreMappingDto): Observable<void | null> {
    const { id, ...updateDto } = dto;
    const baseURL = this.getBaseServiceUrl();
    if (!baseURL) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = `${baseURL}/${id}`;
    const request = this.httpService
      .patch<Omit<UpdateFileStoreMappingDto, 'id'>, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: updateDto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<void | null> => {
          return this._handleResponseError<void | null>(value);
        }),
      );
    return request;
  }

  deleteMapping(dto: DeleteFileStoreMappingDto): Observable<void | null> {
    const { id } = dto;

    const baseURL = this.getBaseServiceUrl();

    if (!baseURL) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }
    const requestUrl = `${baseURL}/${id}`;
    const request = this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<void | null> => {
          return this._handleResponseError<void | null>(value);
        }),
      );
    return request;
  }
}
